import axios from "axios";
import { axiosV2 } from "config/@axios";
import { apiV2Url } from "config/environment";
import storage from "@storage";
import deviceIdGenerator from "utils/helpers/deviceIdGenerator";
import { importLangResources } from "locale";
import { SET_THEME } from "./uiActions";
import {
  SET_POLICIE_DATA,
  SET_UNCONFIRMED_POLICIE_DATA
} from "./policieActions";
import { closeAddAccount } from "layout/store/layoutActions";
import * as uiActions from "store/actions/uiActions";
import { redirect } from "react-router-dom";
import {
  assertionToJSON,
  isWebAuthnLoginSupported,
  NO_PASSKEY_MESSAGE_KEY,
  PasskeyError,
  signalUnknownCredential,
  toRequestOptions
} from "utils/helpers/webauthn";
import {
  isDirectMailboxLogin,
  redirectToWebmail
} from "utils/direct-mailbox-login";

export const LOGIN_SUCCESS = "Auth::LoginSuccess";
export const SET_PERMISSIONS = "Auth::SetPermissions";
export const LOGIN_MFA = "Auth::LoginMfa";
export const ADD_ACCOUNT = "Auth::AddAccount";
export const REMOVE_ACCOUNT = "Auth::RemoveAccount";
export const TRY_LOGIN = "Auth::TryLogin";
export const LOGIN_FAILED = "Auth::LoginFailed";
export const RESET_FAILED = "Auth::ResetFailed";
export const LOGOUT = "Auth::Logout";
export const RESET_SESSION = "Auth::ResetSession";
export const SET_IMAGE = "Auth::SetImage";
export const UPDATE_SETTINGS = "Auth::UpdateSettings";
export const SET_STEP_LOGIN = "Auth::SetLoginStep";
export const SET_ALL_IMAGES_VISIBLE = "Auth::SetAllImagesVisible";
export const SET_PASSWORD_RENEW = "Auth::SetPasswordRenew";
export const SET_PASSWORD_WARNING = "Auth::SetPasswordWarning";
export const SET_CAPTCHA_REQUIRED = "Auth::SetCaptchaRequired";
export const SET_REDIRECT_URL = "Auth::SetRedirectUrl";
export const SET_LOGIN_BAR_BUTTON = "Auth::SetLoginBarButton";
export const SET_SESSION_TIMEOUT = "Auth::SetSessionTimeout";

importLangResources("generic");

const addonPermissions = [
  "MC_USE_ZIMBRA",
  "MC_USE_MAIL",
  "MC_USE_OWA",
  "MC_USE_ARCHIVE",
  "MC_USE_QUARANTINE",
  "MC_USE_STATS",
  "MC_USE_LOGS",
  "MC_USE_CALENDAR",
  "MC_USE_DOCUMENTS",
  "MC_USE_TASKS",
  "MC_USE_BWLIST",
  "MC_USE_CONTACTS",
  "MC_USE_TAGS",
  "MC_MANAGE_PROTOCOLS",
  "MAILBOX_FILTERS"
];

export const resetSession =
  (login = true) =>
  dispatch => {
    storage.clearWithout([
      "accounts",
      "deviceId",
      "notifications",
      "remembered",
      "lang",
      "version",
      "timeoutReturnUrl",
      "timeoutEmail"
    ]);
    //
    // take a look routes/PrivateRouter
    // for redirection
    //

    if (login) {
      dispatch({ type: LOGOUT });

      const accounts = storage.get("accounts") || {};

      if (Object.keys(accounts).length > 0) {
        let email = Object.keys(accounts)[0];
        const accountData = accounts[email];

        // Token yoksa timeout akışına yönlendir
        if (!accountData?.access_token) {
          storage.set("timeoutEmail", email);
          storage.set(
            "timeoutReturnUrl",
            window.location.pathname +
              window.location.search +
              window.location.hash
          );
          dispatch(setLoginStep({ step: "password", email }));
          window.location.replace("/timeout");
          return;
        }

        dispatch(loginSuccess(email, accountData));
        if (window.location.href.includes("/oauth2/authorize")) {
          window.location.reload();
        } else {
          window.location.replace("/mailboxes");
        }
      }
    }
  };

export const setSessionTimeout = payload => ({
  type: SET_SESSION_TIMEOUT,
  payload
});

export const handleSessionTimeout = () => (dispatch, getState) => {
  const { auth } = getState();

  if (window.location.pathname === "/timeout") return;

  const currentEmail = auth?.user?.email;
  if (!currentEmail) return;

  storage.remove("accessToken");
  storage.set(
    "timeoutReturnUrl",
    window.location.pathname + window.location.search + window.location.hash
  );
  storage.set("timeoutEmail", currentEmail);

  // Timeout olan hesabın accounts objesindeki tokenini de sil
  const accounts = storage.get("accounts") || {};
  if (accounts[currentEmail]) {
    delete accounts[currentEmail].access_token;
    storage.set("accounts", accounts);
  }

  dispatch({ type: LOGOUT });
  window.location.replace("/timeout");
};

// Persists a login response as the current session (storage + accounts) and
// returns the LOGIN_SUCCESS payload. Split out of loginSuccess for the direct
// mailbox login, which saves the session for later MC tabs but leaves for
// webmail right away - dispatching LOGIN_SUCCESS would start rendering MC.
const saveLoginSession =
  (data, isLimited = false, isNoMailbox = false) =>
  (dispatch, getState) => {
    const accessToken = data?.access_token;
    const normalizedIsLimited =
      isLimited === true || parseInt(data?.limited_login, 10) === 1;

    const image = data.image;
    let { options = {}, permissions = [], warning } = data;
    const state = getState?.() || {};
    const hasPasswordWarning = Object.prototype.hasOwnProperty.call(
      data || {},
      "warning"
    );
    const isCurrentUserRefresh =
      state.auth?.isLoggedIn &&
      state.auth?.user?.email &&
      data?.client_id === state.auth.user.email;
    const passwordWarning =
      hasPasswordWarning || !isCurrentUserRefresh
        ? warning
        : state.auth?.passwordWarning;

    const settings = {
      ...(storage.get("settings") || {}),
      archive: data?.archive || "",
      bulkmail: data?.bulkmail || "N",
      bulk_sending: data?.bulk_sending || 0,
      bulkmail_expire: data?.bulkmail_expire || 0
    };

    const user = {
      id: data?.id || 0,
      name: data?.name || "",
      email: data?.client_id || "",
      image: image || null,
      organization_name: data?.organization_name || "",
      server_type: data?.server_type || ""
    };
    const totalQuota = data.total_quota;
    const usedQuota = data.used_quota;

    const isPasswordRenewRequired = data.password_must_be_changed
      ? true
      : false;

    if (!!data.bulk_sending) {
      permissions = [...permissions, "addon:bulkmail"];
    }
    if (data.archive === "Y") {
      permissions = [...permissions, "addon:archive"];
    }

    if (data.options?.MC_CUSTOM_APPS?.length > 0) {
      permissions = [...permissions, "addon:MC_CUSTOM_APPS"];
    }

    addonPermissions.forEach(option => {
      if (parseInt(data.options?.[option]) === 1) {
        permissions = [...permissions, `addon:${option}`];
      }
    });
    //
    // take a look routes/StatisRouter
    // for redirection
    //
    storage.set("accessToken", accessToken);
    storage.set("user", user);
    storage.set("options", options);
    storage.set("totalQuota", totalQuota);
    storage.set("usedQuota", usedQuota);
    storage.set("settings", settings);
    storage.set("permissions", permissions);
    storage.set("isLimited", normalizedIsLimited);
    storage.set("isNoMailbox", isNoMailbox);
    storage.set("isPasswordRenewRequired", isPasswordRenewRequired);

    dispatch(
      addAccount({
        [data?.client_id]: {
          ...data,
          isLimited: normalizedIsLimited,
          isNoMailbox
        }
      })
    );

    dispatch(closeAddAccount());

    return {
      accessToken,
      user,
      options,
      totalQuota,
      usedQuota,
      settings,
      permissions,
      isLimited: normalizedIsLimited,
      isNoMailbox,
      isPasswordRenewRequired,
      passwordWarning
    };
  };

export const loginSuccess =
  (email, data, isLimited = false, isNoMailbox = false) =>
  dispatch => {
    const wasSessionTimeout = window.location.pathname === "/timeout";
    const payload = dispatch(saveLoginSession(data, isLimited, isNoMailbox));

    if (wasSessionTimeout) {
      const returnUrl = storage.get("timeoutReturnUrl");
      storage.remove("timeoutReturnUrl");
      storage.remove("timeoutEmail");
      window.location.replace(returnUrl || "/mailboxes");
      return;
    }

    return dispatch({
      type: LOGIN_SUCCESS,
      payload
    });
  };

// Entry point for a fresh login (password, MFA, passkey, QR). Not used when
// restoring or switching accounts: those go to loginSuccess directly, because
// a saved response's preauth url is already expired. A direct mailbox login
// still saves the session so the user can open MC pages in another tab.
export const completeLogin = (email, data) => dispatch => {
  if (isDirectMailboxLogin(data)) {
    dispatch(setLoginStep({ step: "webmailRedirect" }));
    dispatch(saveLoginSession(data));
    redirectToWebmail(email, data);
    return;
  }

  return dispatch(loginSuccess(email, data));
};

export const setRedirectUrl = url => {
  return {
    type: SET_REDIRECT_URL,
    payload: {
      url
    }
  };
};

export const addAccount = payload => {
  return {
    type: ADD_ACCOUNT,
    payload
  };
};

export const removeAccount = email => {
  return {
    type: REMOVE_ACCOUNT,
    payload: {
      email
    }
  };
};

export const loginMfa = (email, data) => {
  const mfa = data.mfa;
  const types = data.types;

  return {
    type: LOGIN_MFA,
    payload: {
      mfa,
      types
    }
  };
};

export const setLoginStep = payload => {
  return {
    type: SET_STEP_LOGIN,
    payload
  };
};

export const setLoginBarButton = payload => {
  return {
    type: SET_LOGIN_BAR_BUTTON,
    payload
  };
};

export const updateSettings = payload => {
  return {
    type: UPDATE_SETTINGS,
    payload
  };
};

export const tryLogin =
  (data, step = "login", fromLimitedLogin) =>
  (dispatch, getState) => {
    const isSessionTimeout = window.location.pathname === "/timeout";
    const { deviceId, deviceName } = deviceIdGenerator();
    const rememberme = data.rememberme;

    dispatch({
      type: TRY_LOGIN
    });

    const mainData = {
      details: 1,
      device_id: deviceId,
      device_name: deviceName
    };

    const email = data.email?.toLowerCase()?.trim();
    const password = data.password;
    let config = {};
    if (data.hash) {
      config = {
        ...mainData,
        hash: data.hash
      };
    } else {
      config = {
        ...mainData,
        email,
        password
      };
    }

    if (data.captcha_code) {
      config = {
        ...config,
        captcha_code: data.captcha_code,
        captcha_key: data.captcha_key
      };
    }
    return axiosV2
      .post("/login", config)
      .then(response => {
        document
          .querySelector("body")
          .setAttribute("data-theme", storage.get("theme") || "light");
        if (rememberme) {
          storage.set("remembered", email);
        }
        if (response?.data?.mfa === 1 || response?.data?.mfa === 2) {
          if (data.hash && !isSessionTimeout) {
            redirect("/login");
          }
          if (
            response?.data?.mfa_limited_login &&
            response?.data?.mfa_limited_login === 1
          ) {
            storage.set("accessToken", response?.data?.access_token);
            dispatch(
              setLoginStep({
                step: "mfaAddForm",
                email: email,
                password: password,
                loginResponse: response?.data
              })
            );
            dispatch(setPermissions(response?.data?.permissions || []));
          } else {
            dispatch(
              setLoginStep({
                step: "mfaForm",
                mfa_values: response.data.types,
                email: email,
                password: password
              })
            );
            dispatch(setCaptchaRequired(null));
            return dispatch(loginMfa(email, response.data));
          }
        } else {
          if (data.hash) {
            redirect("/mailboxes");
          } else if (isSessionTimeout && response.data?.access_token) {
            return dispatch(loginSuccess(email, response.data));
          } else if (response.data?.theme) {
            dispatch({
              type: SET_THEME,
              payload: response.data?.theme
            });
            dispatch({
              type: SET_POLICIE_DATA,
              payload: response.data?.policies ? response.data?.policies : []
            });
            dispatch({
              type: SET_UNCONFIRMED_POLICIE_DATA,
              payload: response.data?.unconfirmed_policies
                ? response.data?.unconfirmed_policies
                : []
            });
            const pin_types = [];
            if (response.data?.pin_types?.mobile) {
              pin_types.push({
                type: "mobile",
                value: response.data?.pin_types?.mobile
              });
            }
            if (response.data?.pin_types?.email) {
              pin_types.push({
                type: "email",
                value: response.data?.pin_types?.email
              });
            }
            dispatch(
              setLoginStep({
                step:
                  response.data?.server_type === "mailbox" ||
                  response.data?.server_type === "exchange"
                    ? "password"
                    : "pinForm",
                email: email,
                via_qr: response.data?.via_qr,
                server_type: response.data?.server_type,
                change_password: response.data?.change_password,
                pin_types,
                captcha_required: null
              })
            );
          } else if (isDirectMailboxLogin(response.data)) {
            dispatch(completeLogin(email, response.data));
          } else if (response?.data?.first_login === 1) {
            dispatch(setRedirectUrl("/settings/general"));
            dispatch(loginSuccess(email, response.data));
          } else {
            dispatch(loginSuccess(email, response.data));
            redirect("/mailboxes");
          }
          return true;
        }
      })
      .catch(err => {
        if (err?.response?.status === 406) {
          if (!fromLimitedLogin) {
            storage.clearWithout([
              "accounts",
              "deviceId",
              "notifications",
              "remembered",
              "lang",
              "version"
            ]);
          }
        }

        if (err?.response?.data?.captcha === 1) {
          dispatch(setCaptchaRequired(Math.floor(Date.now() / 1000)));
        }

        return dispatch({
          type: LOGIN_FAILED,
          payload: {
            errorMessage:
              err?.response?.data?.["extended-code"] === "77.21.601.185"
                ? null
                : err.errorMessage,
            fromLimitedLogin: fromLimitedLogin,
            step
          }
        });
      });
  };

// _login_verify's answer for a credential the server does not have.
const PASSKEY_NOT_FOUND_CODE = "96.0.501.23";

const getExtendedCode = error =>
  error?.response?.data?.["extended-code"] ||
  error?.response?.headers?.["x-extended-code"];

export const isPasskeyNotFoundError = error =>
  getExtendedCode(error) === PASSKEY_NOT_FOUND_CODE;

// With an e-mail the backend narrows allowCredentials down to that account's
// passkeys; it comes back empty when the account has none (or does not exist).
// Without one the list is empty and any discoverable credential may answer.
export const fetchPasskeyLoginOptions = email =>
  axiosV2
    .post("login/passkey/_login_options", email ? { email } : undefined)
    .then(({ data }) => data);

export const hasPasskeyCredentials = options =>
  Array.isArray(options?.allowCredentials) &&
  options.allowCredentials.length > 0;

// The step that holds a passkey sign-in until the account's forced policies
// are accepted.
export const POLICY_CONSENT_STEP = "policyConsent";

// The backend issues the token even while forced policies are unconfirmed;
// like the password screen's box, accepting them is the frontend's to ask.
const hasUnconfirmedForcedPolicies = data => {
  const unconfirmed = data?.unconfirmed_policies || [];
  const forced = (data?.policies || []).filter(policy => policy.force);

  return unconfirmed.length > 0 && forced.length > 0;
};

// What the e-mail step's /login stores for the password screen. SplashScreen
// confirms the unconfirmed ones once the session starts.
const setLoginPolicies = data => dispatch => {
  if (!Array.isArray(data?.unconfirmed_policies)) return;

  dispatch({ type: SET_POLICIE_DATA, payload: data.policies || [] });
  dispatch({
    type: SET_UNCONFIRMED_POLICIE_DATA,
    payload: data.unconfirmed_policies
  });
};

const finishPasskeyLogin = (email, data) => dispatch => {
  if (data?.first_login === 1 && !isDirectMailboxLogin(data)) {
    dispatch(setRedirectUrl("/settings/general"));
  }

  return dispatch(completeLogin(email, data));
};

// Has the browser sign the challenge in `options`, then verifies it. The
// backend tells us who signed in in the verify response. `request` carries
// extra navigator.credentials.get fields (mediation, signal), plus:
// - onAssertion: called once the user has approved and verification starts
// - shouldRemember: asked after a successful verification whether to keep the
//   account as the remembered e-mail, like the "remember me" box does
// - policiesAccepted: the screen already had the user accept the forced
//   policies (the password screen); otherwise an account with unconfirmed ones
//   is held on the consent step before the session starts
// Errors are thrown rather than dispatched so the caller can tell a cancelled
// ceremony apart from a failed verification - a failed verification is the
// only one with a `response`.
const signInWithPasskey =
  (
    options,
    { onAssertion, shouldRemember, policiesAccepted, ...request } = {}
  ) =>
  async dispatch => {
    const { deviceId, deviceName } = deviceIdGenerator();

    const assertion = await navigator.credentials.get({
      ...request,
      publicKey: toRequestOptions(options)
    });

    if (!assertion) {
      throw new PasskeyError("login:passkey.errors.cancelled");
    }

    const assertionJSON = assertionToJSON(assertion);
    onAssertion?.();

    let data;
    try {
      ({ data } = await axiosV2.post("login/passkey/_login_verify", {
        ...assertionJSON,
        device_id: deviceId,
        device_name: deviceName
      }));
    } catch (err) {
      // The passkey was deleted on the server (maybe from another browser) but
      // this device still offers it. Asking the browser to forget it keeps the
      // error from coming back on the next sign-in. Without an rpId the browser
      // used the page's own domain, so the signal does too.
      if (isPasskeyNotFoundError(err)) {
        signalUnknownCredential(
          options.rpId || window.location.hostname,
          assertionJSON.id
        );
      }
      throw err;
    }

    // client_id is the account the chosen credential belongs to - in the
    // usernameless flow the only place the e-mail comes from.
    const email = data?.client_id;

    if (email && shouldRemember?.()) {
      storage.set("remembered", email);
    }

    // The response always carries the account's own mfa setting, so an access
    // token is what tells the two apart: with one the passkey already satisfied
    // the second factor, without one the backend is asking for it.
    if (!data?.access_token && (data?.mfa === 1 || data?.mfa === 2)) {
      dispatch(
        setLoginStep({
          step: "mfaForm",
          mfa_values: data.types,
          email
        })
      );
      dispatch(setCaptchaRequired(null));
      return dispatch(loginMfa(email, data));
    }

    dispatch(setLoginPolicies(data));

    // The token stays in the store only, so leaving the page drops it.
    if (!policiesAccepted && hasUnconfirmedForcedPolicies(data)) {
      dispatch(
        setLoginStep({
          step: POLICY_CONSENT_STEP,
          email,
          loginResponse: data
        })
      );
      return;
    }

    return dispatch(finishPasskeyLogin(email, data));
  };

// The user accepted the policies on the consent step: start the session the
// passkey opened.
export const acceptPasskeyLoginPolicies = () => (dispatch, getState) => {
  const { email, loginResponse } = getState().auth.login;

  return dispatch(finishPasskeyLogin(email, loginResponse));
};

// The user turned the policies down: end the session the passkey opened and go
// back to the e-mail screen. The unconfirmed list is cleared at once, or the
// next sign-in's SplashScreen would confirm it for whoever signs in.
export const declinePasskeyLoginPolicies = () => (dispatch, getState) => {
  const token = getState().auth.login.loginResponse?.access_token;

  dispatch({ type: SET_UNCONFIRMED_POLICIE_DATA, payload: [] });
  dispatch(setLoginStep({ step: "login" }));
  dispatch(uiActions.setTheme(""));

  if (token) {
    // Bypasses axiosV2: a 401 there would start the session timeout flow for a
    // session the page never had.
    axios
      .delete(`${apiV2Url}/logout`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Device-ID": deviceIdGenerator().deviceId
        }
      })
      .catch(() => {});
  }
};

// Passkey login from the button: get the options, then run the ceremony.
// Without an e-mail it is usernameless - the account is whichever discoverable
// credential the user picks. With one (the password screen) the browser only
// offers that account's passkeys. `request` is as in signInWithPasskey.
export const loginWithPasskey =
  (accountEmail, request = {}) =>
  async dispatch => {
    if (!isWebAuthnLoginSupported()) {
      throw new PasskeyError("settings:passkeys.errors.not_supported");
    }

    const options = await fetchPasskeyLoginOptions(accountEmail);

    // An empty list would turn the scoped ceremony into a usernameless one and
    // let the user pick another account's passkey. It happens when the passkeys
    // were removed after the password screen checked for them.
    if (accountEmail && !hasPasskeyCredentials(options)) {
      throw new PasskeyError(NO_PASSKEY_MESSAGE_KEY);
    }

    return dispatch(signInWithPasskey(options, request));
  };

// Passkey login from the input's autofill dropdown (conditional mediation).
// The request stays pending without any browser dialog until the user picks a
// passkey from the list; `signal` aborts it. `request` is as in
// signInWithPasskey.
export const loginWithPasskeyAutofill =
  (options, signal, request = {}) =>
  dispatch =>
    dispatch(
      signInWithPasskey(options, {
        ...request,
        mediation: "conditional",
        signal
      })
    );

export const logout = () => dispatch => {
  if (storage.get("accessToken"))
    return axiosV2.delete("/logout").finally(() => {
      dispatch(resetSession());
      dispatch(uiActions.setTheme(""));
    });
  else {
    dispatch(resetSession());
    dispatch(uiActions.setTheme(""));
  }
};

export const setAllImagesVisible = payload => dispatch => {
  storage.set("isAllImagesVisible", payload);
  dispatch({ type: SET_ALL_IMAGES_VISIBLE, payload });
};

export const resetFailed = () => ({
  type: RESET_FAILED
});

export const setImage = image => {
  storage.update("user", {
    image
  });

  return {
    type: SET_IMAGE,
    payload: {
      image
    }
  };
};

export const setRenewRequired = isPasswordRenewRequired => {
  storage.set("isPasswordRenewRequired", isPasswordRenewRequired);

  return {
    type: SET_PASSWORD_RENEW,
    payload: isPasswordRenewRequired
  };
};

export const setCaptchaRequired = timestamp => {
  return {
    type: SET_CAPTCHA_REQUIRED,
    payload: {
      timestamp
    }
  };
};

export const setPasswordWarning = payload => {
  return {
    type: SET_PASSWORD_WARNING,
    payload
  };
};

export const setPermissions = payload => {
  return {
    type: SET_PERMISSIONS,
    payload
  };
};
